import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authApi, organizationApi, processingApi, tomApi, dsrApi, breachApi } from '../services/api';
import OnboardingWidget from '../components/OnboardingWidget';
import { User, Organization } from '@dsgvo-buddy/types';
import {
  Card,
  CardHeader,
  Title,
  Text,
  FlexBox,
  Button,
  Icon,
  BusyIndicator,
  MessageStrip
} from '@ui5/webcomponents-react';
import QuickCreateDialog from '../components/QuickCreateDialog';
import '@ui5/webcomponents-icons/dist/AllIcons.js';
import PageIntro from '../components/PageIntro';
import { pageIntros } from '../helpers/helpTexts';

export default function DashboardPage() {
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [processingCount, setProcessingCount] = useState(0);
  const [tomCount, setTomCount] = useState(0);
  const [dsrCount, setDsrCount] = useState(0);
  const [breachCount, setBreachCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [quickCreateType, setQuickCreateType] = useState<string | null>(null);
  const [quickCreateOpen, setQuickCreateOpen] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [userResponse, orgsResponse] = await Promise.all([
        authApi.getCurrentUser(),
        organizationApi.getAll()
      ]);

      if (userResponse.success && userResponse.data) {
        setUser(userResponse.data);
      } else {
        navigate('/login');
        return;
      }

      if (orgsResponse.success && orgsResponse.data) {
        setOrganizations(orgsResponse.data);
        if (orgsResponse.data.length === 0) {
          navigate('/setup');
          return;
        }
        // Load processing and TOM counts
        const org = orgsResponse.data[0];
        const [procResponse, tomResponse, dsrResponse, breachResponse] = await Promise.all([
          processingApi.getByOrganization(org.id),
          tomApi.getByOrganization(org.id),
          dsrApi.getByOrganization(org.id),
          breachApi.getByOrganization(org.id)
        ]);
        if (procResponse.success && procResponse.data) {
          setProcessingCount(procResponse.data.length);
        }
        if (tomResponse.success && tomResponse.data) {
          setTomCount(tomResponse.data.length);
        }
        if (dsrResponse.success && dsrResponse.data) {
          setDsrCount(dsrResponse.data.length);
        }
        if (breachResponse.success && breachResponse.data) {
          setBreachCount(breachResponse.data.filter(b => b.status !== 'closed').length);
        }
      }
    } catch (error) {
      navigate('/login');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <FlexBox alignItems="Center" justifyContent="Center" style={{ height: '100%' }}>
        <BusyIndicator active size="Large" />
      </FlexBox>
    );
  }

  return (
    <div className="page-layout--detail">
      <Title level="H3" style={{ marginBottom: '0.25rem' }}>
        Willkommen, {user?.name}!
      </Title>
      <Text style={{ color: 'var(--sapNeutralTextColor)', marginBottom: '1.5rem', display: 'block' }}>
        Ihr DSGVO-Compliance-Dashboard
      </Text>

      <PageIntro {...pageIntros.dashboard} storageKey="dashboard" />

      {/* Onboarding Widget */}
      <div style={{ marginBottom: '1.5rem' }}>
        <OnboardingWidget />
      </div>

      {/* KPI Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem', marginBottom: '1.5rem' }}>
        <Card
          header={<CardHeader titleText="Organisationen" subtitleText="Aktive Einheiten" avatar={<Icon name="factory" />} interactive onClick={() => navigate('/organization')} />}
        >
          <div style={{ padding: '0.25rem 1.25rem 1.25rem' }}>
            <Text style={{ fontSize: '2rem', fontWeight: '700', color: 'var(--sapBrandColor)' }}>
              {organizations.length}
            </Text>
          </div>
        </Card>

        <Card
          header={<CardHeader titleText="Verarbeitungen" subtitleText="Erfasste Tätigkeiten" avatar={<Icon name="documents" />} interactive onClick={() => navigate('/processing')} />}
        >
          <div style={{ padding: '0.25rem 1.25rem 1.25rem' }}>
            <Text style={{ fontSize: '2rem', fontWeight: '700', color: 'var(--sapPositiveColor)' }}>
              {processingCount}
            </Text>
          </div>
        </Card>

        <Card
          header={<CardHeader titleText="TOM" subtitleText="Sicherheitsmaßnahmen" avatar={<Icon name="shield" />} interactive onClick={() => navigate('/tom')} />}
        >
          <div style={{ padding: '0.25rem 1.25rem 1.25rem' }}>
            <Text style={{ fontSize: '2rem', fontWeight: '700', color: 'var(--sapInformativeColor)' }}>
              {tomCount}
            </Text>
          </div>
        </Card>

        <Card
          header={<CardHeader titleText="Betroffenenrechte" subtitleText="Offene Anfragen" avatar={<Icon name="person-placeholder" />} interactive onClick={() => navigate('/data-subject-requests')} />}
        >
          <div style={{ padding: '0.25rem 1.25rem 1.25rem' }}>
            <Text style={{ fontSize: '2rem', fontWeight: '700', color: 'var(--sapCriticalColor)' }}>
              {dsrCount}
            </Text>
          </div>
        </Card>

        <Card
          header={<CardHeader titleText="Vorfälle" subtitleText="Offene Vorfälle" avatar={<Icon name="alert" />} interactive onClick={() => navigate('/breaches')} />}
        >
          <div style={{ padding: '0.25rem 1.25rem 1.25rem' }}>
            <Text style={{ fontSize: '2rem', fontWeight: '700', color: 'var(--sapCriticalColor)' }}>
              {breachCount}
            </Text>
          </div>
        </Card>
      </div>

      <FlexBox wrap="Wrap" style={{ gap: '1rem' }}>
        {/* Profil Card */}
        <Card
          style={{ flex: '1 1 340px' }}
          header={<CardHeader titleText="Ihr Profil" avatar={<Icon name="person-placeholder" />} />}
        >
          <div style={{ padding: '0.25rem 1.25rem 1.25rem' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '0.5rem 1rem', alignItems: 'baseline' }}>
              <Text style={{ fontWeight: '600', color: 'var(--sapNeutralTextColor)', fontSize: '0.8125rem' }}>E-Mail</Text>
              <Text>{user?.email}</Text>
              <Text style={{ fontWeight: '600', color: 'var(--sapNeutralTextColor)', fontSize: '0.8125rem' }}>Rolle</Text>
              <Text style={{ textTransform: 'capitalize' }}>{user?.role}</Text>
              <Text style={{ fontWeight: '600', color: 'var(--sapNeutralTextColor)', fontSize: '0.8125rem' }}>Registriert</Text>
              <Text>{user?.created_at ? new Date(user.created_at).toLocaleDateString('de-DE') : '-'}</Text>
            </div>
          </div>
        </Card>

        {/* Schnellzugriff Card */}
        <Card
          style={{ flex: '1 1 340px' }}
          header={<CardHeader titleText="Schnellzugriff" avatar={<Icon name="initiative" />} />}
        >
          <div style={{ padding: '0.25rem 1.25rem 1.25rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            <Button icon="factory" design="Default" onClick={() => navigate('/organization')} style={{ justifyContent: 'flex-start' }}>
              Organisation verwalten
            </Button>
            <Button icon="documents" design="Default" onClick={() => navigate('/processing')} style={{ justifyContent: 'flex-start' }}>
              Verarbeitungen ansehen
            </Button>
            <Button icon="shield" design="Default" onClick={() => navigate('/tom')} style={{ justifyContent: 'flex-start' }}>
              TOM-Maßnahmen ansehen
            </Button>
            <Button icon="person-placeholder" design="Default" onClick={() => navigate('/data-subject-requests')} style={{ justifyContent: 'flex-start' }}>
              Betroffenenrechte ansehen
            </Button>
            <Button icon="alert" design="Default" onClick={() => navigate('/breaches')} style={{ justifyContent: 'flex-start' }}>
              Vorfälle ansehen
            </Button>
            <Button icon="add-document" design="Emphasized" onClick={() => { setQuickCreateType('processing'); setQuickCreateOpen(true); }} style={{ justifyContent: 'flex-start' }}>
              Neue Verarbeitung
            </Button>
            <Button icon="activity-individual" design="Emphasized" onClick={() => { setQuickCreateType('dsr'); setQuickCreateOpen(true); }} style={{ justifyContent: 'flex-start' }}>
              Neue Anfrage
            </Button>
            <Button icon="alert" design="Negative" onClick={() => { setQuickCreateType('breach'); setQuickCreateOpen(true); }} style={{ justifyContent: 'flex-start' }}>
              Vorfall melden
            </Button>
            <Button icon="print" design="Default" onClick={() => navigate('/reports')} style={{ justifyContent: 'flex-start' }}>
              Berichte und Export
            </Button>
          </div>
        </Card>
      </FlexBox>

      {/* Getting Started */}
      <Card style={{ marginTop: '1rem' }} header={<CardHeader titleText="Erste Schritte" avatar={<Icon name="light-bulb" />} />}>
        <div style={{ padding: '0.25rem 1.25rem 1.25rem' }}>
          <MessageStrip design="Information" hideCloseButton>
            Willkommen bei DSGVOeasy! Beginnen Sie mit der Einrichtung Ihrer Organisation und erfassen Sie Ihre Verarbeitungstätigkeiten gemäß Art. 30 DSGVO.
          </MessageStrip>
        </div>
      </Card>

      {quickCreateType && (
        <QuickCreateDialog
          type={quickCreateType as 'processing' | 'service-provider' | 'tom' | 'dsr' | 'breach'}
          organizationId={organizations[0]?.id}
          open={quickCreateOpen}
          onClose={() => setQuickCreateOpen(false)}
          onCreated={(id) => {
            setQuickCreateOpen(false);
            const routes: Record<string, string> = {
              processing: `/processing/${id}`,
              dsr: `/data-subject-requests/${id}`,
              breach: `/breaches/${id}`
            };
            const route = routes[quickCreateType];
            if (route) navigate(route);
          }}
        />
      )}
    </div>
  );
}
